/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { showToast } from 'reducers/toasts';
import AdminUpgradeQuoteBox from 'admin/components/AdminUpgradeQuoteBox';
import AdminUpgradeQuoteTotalBox from 'admin/components/AdminUpgradeQuoteTotalBox';
import AdminUpgradeShareQuoteBanner from 'admin/components/AdminUpgradeShareQuoteBanner';
import Row from 'components/Row';

import type { Fields } from 'admin/reducers/adminUpgradePage';
import {
  saveQuoteToBackend,
  saveLocalQuoteField,
  ORG_TYPE_STRING,
} from 'admin/reducers/adminUpgradePage';
import EventTracking from 'lib/EventTracking';

type Props = {
  quote: *,
  fields: Fields,
  pageIndex: number,
  saveLocalQuoteField: (name: string, value: *) => void,
  showToast: (text: string, kind: 'success' | 'error') => void,
  saveQuoteToBackend: (quoteUUID?: ?string, fields: Fields, pageIndex: number) => Promise<any>,
  orgTypeString: string,
};

type State = {
  persistingTaxExemption?: boolean,
};

class AdminUpgradeReviewQuoteForm extends React.Component<Props, State> {
  state: State = {
    persistingTaxExemption: false,
  };

  static trackingPrefix = 'admin_self_serve.review_quote';

  componentDidMount() {
    this.track('view');
  }

  track = (key: string) => {
    EventTracking.trackKey(`${AdminUpgradeReviewQuoteForm.trackingPrefix}.${key}`);
  };

  /* This is a bit different then the other pages.
   * We want this form to auto-update when the tax checkbox
   * is clicked. This means that instead of waiting for page change
   * to save the quote, we save the quote on every click. Which causes
   * the quote in the redux store to get update with the new
   * tax value & total price.
   */
  handleTaxChange = (event: *) => {
    event.persist();
    this.track('tax_exempt.click');

    // compute the new fields map with the new value
    const updatedFields = this.props.fields.set('taxExempt', event.target.checked);
    this.setState({ persistingTaxExemption: true });

    this.props
      .saveQuoteToBackend(this.props.quote.uuid, updatedFields, this.props.pageIndex)
      .then(result => {
        this.setState({ persistingTaxExemption: false });

        if (result.error) {
          console.debug(result.error);
          console.error('AdminUpgradeReviewQuoteForm - could not update tax exemption');
          this.props.showToast('Oops, an error occured, could not change tax exemption', 'error');
        } else {
          this.props.saveLocalQuoteField('taxExempt', event.target.checked);
        }
      })
      .catch(e => {
        console.debug(e, e && e.stack);
        console.error('AdminUpgradeReviewQuoteForm - hard fail - could not update tax exemption');
        this.props.showToast('Oops, an error occured, could not change tax exemption', 'error');
        this.setState({ persistingTaxExemption: false });
      });
  };

  render() {
    const { quote, orgTypeString } = this.props;
    const address = [quote.organization.city, quote.organization.state].filter(Boolean).join(', ');
    return (
      <div>
        <Row size="medium">
          <AdminUpgradeQuoteBox
            orgName={quote.organization.name}
            orgAddress={address}
            orgType={orgTypeString}
            startDate={quote.startDate}
            endDate={quote.endDate}
            price={quote.price}
            taxExempt={quote.taxExempt}
            tax={quote.tax}
            persistingTaxExemption={!!this.state.persistingTaxExemption}
            handleTaxChange={this.handleTaxChange}
            editable
          />
          <AdminUpgradeQuoteTotalBox quote={quote} />
        </Row>
        <AdminUpgradeShareQuoteBanner
          shareToken={quote.shareToken}
          showToast={this.props.showToast}
          orgName={quote.organization.name}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    fields: state.adminUpgradePage.fields,
    quote: state.adminUpgradePage.quote,
    pageIndex: state.adminUpgradePage.pageIndex,
    orgTypeString: ORG_TYPE_STRING[state.adminUpgradePage.fields.get('orgType')],
  };
}

function mapDispatchToProps(dispatch: Function) {
  // $FlowIssue
  return bindActionCreators({ saveQuoteToBackend, showToast, saveLocalQuoteField }, dispatch);
}

export { AdminUpgradeReviewQuoteForm };
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminUpgradeReviewQuoteForm);
